import type { QueryData } from "@supabase/supabase-js"
import { useInfiniteQuery, useQuery } from "@tanstack/react-query"

import { type ShiftWithDisplay } from "@/hooks/useShiftDialog"
import { supabase } from "@/lib/supabase"
import type { Shift } from "@/services/shiftsService"
import { getShiftDisplayStatus } from "@/utils/shiftDisplayStatus.ts"

import type { Tables } from "../../database.types"
import { USABLE_OPERATIONAL_STATUSES } from "../../shared/vehicle.ts"

export type Vehicle = Tables<"vehicles">
export type VehicleProvider = Tables<"vehicle_providers">

export interface VehicleListRow {
  vehicle: Vehicle
  depotName: Tables<"depots">["name"] | null
  carParkName: Tables<"car_parks">["name"] | null
  zerityReference: VehicleProvider["provider_reference"] | null
  inversReference: VehicleProvider["provider_reference"] | null
  assignedTo: VehicleAssignment | null
  lastFailedCommand: FailedCommand | null
}

export interface VehicleAssignment {
  personId: string | null
  personName: string | null
  shiftStart: string
  shiftEnd: string
  clockedOut: boolean
}

export interface FailedCommand {
  eventType: string
  result: string
  failureReason: string | null
  createdAt: string
}

export interface VehicleFilters {
  depotId?: string
  vehicleGroup?: string
  keylessOnly?: boolean
  operationalStatuses?: string[]
  search?: string
}

export function normaliseRegistration(registration: string | null): string {
  return (registration ?? "").replace(/\s/g, "").toUpperCase()
}

export function useVehicles(filters: VehicleFilters = {}) {
  return useQuery({
    queryKey: ["vehicles", filters],
    queryFn: async (): Promise<VehicleListRow[]> => {
      let query = supabase
        .from("vehicles")
        .select("*, depots(name), car_parks(name), vehicle_providers(provider, provider_reference)")
        .is("vehicle_providers.deactivated_at", null)

      if (filters.depotId) query = query.eq("depot_id", filters.depotId)
      if (filters.vehicleGroup) query = query.eq("vehicle_group", filters.vehicleGroup)

      const statuses = filters.operationalStatuses ?? USABLE_OPERATIONAL_STATUSES
      if (statuses.length > 0) query = query.in("operational_status", statuses)

      const { data: vehicles, error } = await query.order("registration")
      if (error) throw error

      const vehicleIds = vehicles.map((v) => v.id)
      if (vehicleIds.length === 0) return []

      const nowIso = new Date().toISOString()

      // Failures since yesterday. Older ones are history, not something ops still act on.
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()

      const [{ data: shifts, error: shiftsError }, { data: failures, error: failuresError }] =
        await Promise.all([
          supabase
            .from("shifts")
            .select(
              "id, vehicle_id, person_id, start_time, end_time, status, person:people!shifts_person_id_fkey(person), timesheets!timesheets_shift_id_fkey(clocked_out_time)",
            )
            .in("vehicle_id", vehicleIds)
            .eq("status", "Filled")
            .lte("start_time", nowIso)
            .gte("end_time", nowIso),
          supabase
            .from("vehicle_keyless_audit_log")
            .select("vehicle_id, event_type, result, failure_reason, created_at")
            .in("vehicle_id", vehicleIds)
            .in("event_type", ["lock", "unlock"])
            .in("result", ["failure", "refused"])
            .gte("created_at", since)
            .order("created_at", { ascending: false }),
        ])

      if (shiftsError) throw shiftsError
      if (failuresError) throw failuresError

      const shiftByVehicle = new Map<string, VehicleAssignment>()
      for (const shift of shifts ?? []) {
        if (!shift.vehicle_id || shiftByVehicle.has(shift.vehicle_id)) continue

        shiftByVehicle.set(shift.vehicle_id, {
          personId: shift.person_id,
          personName: shift.person?.person ?? null,
          shiftStart: shift.start_time,
          shiftEnd: shift.end_time,
          clockedOut: (shift.timesheets ?? []).some((t) => Boolean(t.clocked_out_time)),
        })
      }

      // Ordered newest first above, so the first failure seen per vehicle is the latest.
      const failureByVehicle = new Map<string, FailedCommand>()
      for (const failure of failures ?? []) {
        if (failureByVehicle.has(failure.vehicle_id)) continue
        failureByVehicle.set(failure.vehicle_id, {
          eventType: failure.event_type,
          result: failure.result,
          failureReason: failure.failure_reason,
          createdAt: failure.created_at,
        })
      }

      const rows: VehicleListRow[] = vehicles.map((row) => {
        // The joins ride along on the row; keep them off the Vehicle shape.
        const { depots, car_parks, vehicle_providers, ...vehicle } = row as typeof row & {
          depots: { name: string } | null
          car_parks: { name: string } | null
          vehicle_providers: Pick<VehicleProvider, "provider" | "provider_reference">[]
        }
        const referenceByProvider = new Map(
          (vehicle_providers ?? []).map((p) => [p.provider, p.provider_reference]),
        )

        return {
          vehicle,
          depotName: depots?.name ?? null,
          carParkName: car_parks?.name ?? null,
          zerityReference: referenceByProvider.get("zerity") ?? null,
          inversReference: referenceByProvider.get("invers") ?? null,
          assignedTo: shiftByVehicle.get(vehicle.id) ?? null,
          lastFailedCommand: failureByVehicle.get(vehicle.id) ?? null,
        }
      })

      // Keyless means a live INVERS row, which is exactly what inversReference records.
      const keylessFiltered = filters.keylessOnly
        ? rows.filter((r) => Boolean(r.inversReference))
        : rows

      const search = filters.search?.trim().toLowerCase()
      if (!search) return keylessFiltered

      // Registration matches ignore spacing, so "HV21ABC" and "HV21 ABC" both find the same van.
      const searchRegistration = normaliseRegistration(search)
      return keylessFiltered.filter(
        (row) =>
          row.vehicle.registration.includes(searchRegistration) ||
          row.assignedTo?.personName?.toLowerCase().includes(search),
      )
    },
    staleTime: 60 * 1000,
  })
}

const AUDIT_LOG_PAGE_SIZE = 50
const VEHICLE_SHIFTS_LIMIT = 50

export interface VehicleAuditEntry extends Tables<"vehicle_keyless_audit_log"> {
  actorName: string | null
}

// A busy keyless van logs every lock and unlock, so the history pages rather than loading whole.
export function useVehicleAuditLog(vehicleId: string | undefined) {
  return useInfiniteQuery({
    queryKey: ["vehicle-audit-log", vehicleId],
    enabled: Boolean(vehicleId),
    initialPageParam: 0,
    getNextPageParam: (lastPage: VehicleAuditEntry[], allPages: VehicleAuditEntry[][]) =>
      lastPage.length < AUDIT_LOG_PAGE_SIZE ? undefined : allPages.flat().length,
    queryFn: async ({ pageParam }): Promise<VehicleAuditEntry[]> => {
      const { data, error } = await supabase
        .from("vehicle_keyless_audit_log")
        .select("*, people(person)")
        .eq("vehicle_id", vehicleId!)
        .order("created_at", { ascending: false })
        .range(pageParam, pageParam + AUDIT_LOG_PAGE_SIZE - 1)

      if (error) throw error

      return (data ?? []).map((row) => {
        // people RLS is role-scoped and does not know about view.page.vehicles, so a team-only user
        // gets no actor row even though the audit row itself is readable.
        const { people, ...entry } = row as typeof row & { people: { person: string } | null }
        return { ...entry, actorName: people?.person ?? null }
      })
    },
  })
}

const vehicleShiftsQuery = () =>
  supabase.from("shifts").select(`
  id, person_id, start_time, end_time, minutes_late, awol, status, area, type, employer, location,
  depot_id, notes, preassigned_route, cancelled_reason, cancelled_time, notifications,
  vehicles!shifts_vehicle_id_fkey(id, registration_display),
  last_edited_by,
  people!shifts_person_id_fkey(person, person_id, employer, status, sheriff_type, primary_person_id),
  timesheets!timesheets_shift_id_fkey(*)
`)

// Derived from the select rather than hand-written, so a column added above cannot drift from it.
type VehicleShiftRow = QueryData<ReturnType<typeof vehicleShiftsQuery>>[number]

function toShift(row: VehicleShiftRow): ShiftWithDisplay {
  const person = row.people
  // Joined from timesheets.shift_id, the side the sheriff board reads, so the clocked times agree
  // with it. shifts.timesheets_id points the other way and is not always filled in.
  const timesheet = row.timesheets?.[0]

  const shift: Shift = {
    id: row.id,
    peopleId: row.person_id,
    startTime: row.start_time,
    endTime: row.end_time,
    minutesLate: row.minutes_late,
    awol: row.awol,
    status: row.status,
    area: row.area,
    type: row.type,
    employer: row.employer,
    location: row.location,
    depotId: row.depot_id,
    // The plate comes off the FK, so it is the vehicle's own registration rather than free text.
    vehicle: row.vehicles
      ? { id: row.vehicles.id, reg: row.vehicles.registration_display }
      : undefined,
    notes: row.notes,
    preassignedRoute: row.preassigned_route,
    cancelledReason: row.cancelled_reason,
    cancelledTime: row.cancelled_time,
    notifications: row.notifications,
    lastEditedBy: row.last_edited_by,
    people: person
      ? {
          employer: person.employer,
          personId: person.person_id,
          person: person.person,
          status: person.status,
          type: person.sheriff_type,
          primaryPersonId: person.primary_person_id,
        }
      : undefined,
    timesheet: timesheet
      ? {
          id: timesheet.id,
          clockedInTime: timesheet.clocked_in_time,
          clockedOutTime: timesheet.clocked_out_time,
          clockinDepotId: timesheet.clockin_depot_id,
          clockinCarParkId: timesheet.clockin_car_park_id,
          clockoutDepotId: timesheet.clockout_depot_id,
          break: timesheet.break,
          shiftId: timesheet.shift_id,
          personId: timesheet.person_id,
          latitude: timesheet.latitude,
          longitude: timesheet.longitude,
          endLatitude: timesheet.end_latitude,
          endLongitude: timesheet.end_longitude,
          status: timesheet.status,
          lastEditedBy: timesheet.last_edited_by,
          autoClockOut: timesheet.auto_clock_out,
          createdAt: timesheet.created_at,
          updatedAt: timesheet.updated_at,
          employer: timesheet.employer,
        }
      : undefined,
  }

  return { ...shift, displayStatus: getShiftDisplayStatus(shift) }
}

export function useVehicleShifts(vehicleId: string) {
  return useQuery({
    queryKey: ["vehicle-shifts", vehicleId],
    enabled: Boolean(vehicleId),
    staleTime: 60 * 1000,
    queryFn: async (): Promise<{ upcoming: ShiftWithDisplay[]; past: ShiftWithDisplay[] }> => {
      const now = new Date().toISOString()
      // A cancelled block never put anyone in the van, so it is noise in both directions.
      const matching = () =>
        vehicleShiftsQuery()
          .eq("vehicle_id", vehicleId!)
          .neq("status", "Cancelled")
          .limit(VEHICLE_SHIFTS_LIMIT)

      const [upcoming, past] = await Promise.all([
        matching().gte("end_time", now).order("start_time"),
        matching().lt("end_time", now).order("start_time", { ascending: false }),
      ])

      if (upcoming.error) throw upcoming.error
      if (past.error) throw past.error

      return {
        upcoming: (upcoming.data ?? []).map(toShift),
        past: (past.data ?? []).map(toShift),
      }
    },
  })
}
